import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Calendar, Trophy, TrendingUp, Loader2, Sparkles, PartyPopper, Heart, Frown, ThumbsDown, Star, Info, Search } from 'lucide-react';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { MatchdayDetailsModal } from './MatchdayDetailsModal';
import { TeamEventsBreakdownModal } from './TeamEventsBreakdownModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente animazione vittoria
const WinAnimation = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
    <div className="animate-bounce">
      <Trophy className="h-20 w-20 text-yellow-500 drop-shadow-2xl animate-pulse" />
    </div>
    <div className="absolute inset-0 flex items-center justify-center">
      <Star className="h-12 w-12 text-yellow-400 animate-spin" style={{ animationDuration: '3s' }} />
    </div>
  </div>
);

// Componente animazione sconfitta
const LossAnimation = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
    <div className="animate-pulse">
      <Frown className="h-20 w-20 text-gray-400 drop-shadow-lg" />
    </div>
  </div>
);

// Componente animazione pareggio
const DrawAnimation = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
    <div className="animate-bounce" style={{ animationDelay: '0.1s' }}>
      <Heart className="h-16 w-16 text-yellow-500 drop-shadow-lg" />
    </div>
  </div>
);

export const GiornateTab = forwardRef(({ leagueId, token, user, isAdmin, teams, onMatchdayCalculated }, ref) => {
  const [matchdays, setMatchdays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [animatingMatch, setAnimatingMatch] = useState(null); // { matchId, type: 'win'|'loss'|'draw' }
  const [selectedMatchday, setSelectedMatchday] = useState(null);
  const [showMatchdayModal, setShowMatchdayModal] = useState(false);
  const [breakdownModal, setBreakdownModal] = useState({ isOpen: false, teamId: null, teamName: '' });
  const { width, height } = useWindowSize();
  
  // Trova il team dell'utente corrente
  const userTeam = teams?.find(t => t.owner_id === user?.id || t.partner_id === user?.id);

  const fetchMatchdays = useCallback(async () => {
    try {
      console.log('🔍 GiornateTab: Fetching matchdays for league', leagueId);
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/matchdays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('📡 GiornateTab: Response status', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ GiornateTab: Matchdays received', data);
        setMatchdays(data);
      } else {
        const error = await response.text();
        console.error('❌ GiornateTab: Error response', error);
      }
    } catch (err) {
      console.error('❌ GiornateTab: Error fetching matchdays:', err);
    } finally {
      setIsLoading(false);
    }
  }, [leagueId, token]);

  // Esporre il metodo di refresh tramite ref
  useImperativeHandle(ref, () => ({
    refreshMatchdays: fetchMatchdays
  }));

  useEffect(() => {
    fetchMatchdays();
  }, [fetchMatchdays]);

  const handleCreateMatchday = async () => {
    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/matchdays/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Nuova giornata creata!');
        await fetchMatchdays();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella creazione della giornata');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCalculateMatchday = async (matchdayId) => {
    setIsCalculating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/leagues/${leagueId}/matchdays/${matchdayId}/calculate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setShowConfetti(true);
        toast.success('🎉 Giornata calcolata! Risultati disponibili!');
        await fetchMatchdays();
        
        // Notify parent to refresh championship standings and leaderboard
        if (onMatchdayCalculated) {
          await onMatchdayCalculated();
        }
        
        // Stop confetti after 5 seconds
        setTimeout(() => setShowConfetti(false), 5000);
        
        // Trigger animations for user's match
        const updatedMatchdays = await response.json();
        if (updatedMatchdays && updatedMatchdays.matches) {
          const userMatch = updatedMatchdays.matches.find(
            m => m.team1_id === userTeam?.id || m.team2_id === userTeam?.id
          );
          if (userMatch) {
            const isTeam1 = userMatch.team1_id === userTeam?.id;
            const myTeamId = isTeam1 ? userMatch.team1_id : userMatch.team2_id;
            
            let animType = 'loss';
            if (userMatch.winner === myTeamId) {
              animType = 'win';
            } else if (userMatch.winner === 'draw') {
              animType = 'draw';
            }
            
            setAnimatingMatch({ matchId: userMatch.id, type: animType });
            setTimeout(() => setAnimatingMatch(null), 4000);
          }
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel calcolo della giornata');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleResetMatchday = async (matchdayId) => {
    if (!confirm('Sei sicuro di voler annullare questa giornata? I punti campionato verranno rimossi ma i punti fantasy dei professori rimarranno.')) {
      return;
    }

    setIsCalculating(true);
    try {
      const response = await fetch(`${API_URL}/api/matchdays/${matchdayId}/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Giornata resettata con successo!');
        await fetchMatchdays();
        
        // Notify parent to refresh championship standings and leaderboard
        if (onMatchdayCalculated) {
          await onMatchdayCalculated();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel reset della giornata');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setIsCalculating(false);
    }
  };


  const getMatchResult = (match, teamId) => {
    if (match.status !== 'completed') return null;

    const isTeam1 = match.team1_id === teamId;
    const isTeam2 = match.team2_id === teamId;

    if (!isTeam1 && !isTeam2) return null;

    const myTeamId = isTeam1 ? match.team1_id : match.team2_id;
    
    if (match.winner === myTeamId) {
      return { type: 'win', text: 'HAI VINTO! 🎉', color: 'text-green-600', bgColor: 'bg-green-50' };
    } else if (match.winner === 'draw') {
      return { type: 'draw', text: 'PAREGGIO 🤝', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    } else {
      return { type: 'loss', text: 'HAI PERSO 😢', color: 'text-red-600', bgColor: 'bg-red-50' };
    }
  };

  const findMyMatch = (matchday) => {
    return matchday.matches.find(
      (m) => m.team1_id === userTeam?.id || m.team2_id === userTeam?.id
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const pendingMatchday = matchdays.find((m) => m.status === 'pending');
  const lastCalculated = matchdays.find((m) => m.status === 'calculated');
  const allMatchdays = matchdays.filter((m) => m.status === 'calculated').slice(0, 5);

  console.log('🎨 GiornateTab: Rendering with', {
    isLoading,
    matchdaysCount: matchdays.length,
    hasPending: !!pendingMatchday,
    hasLastCalculated: !!lastCalculated,
    user: user?.id,
    userTeam: userTeam?.id,
    isAdmin
  });

  return (
    <div className="space-y-6">
      {showConfetti && <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />}

      {/* Admin Controls */}
      {isAdmin && (
        <Card className="border-2 border-blue-500 shadow-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Sparkles className="h-5 w-5 text-blue-600" />
              Gestione Giornate (Admin)
            </CardTitle>
            <CardDescription className="text-sm">Crea nuove giornate e calcola i risultati</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={handleCreateMatchday}
              disabled={isCreating || !!pendingMatchday}
              className="w-full transition-all duration-300 shadow-lg text-base py-6"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <Calendar className="mr-2 h-5 w-5" />
                  Crea Nuova Giornata
                </>
              )}
            </Button>

            {pendingMatchday && (
              <Button
                onClick={() => handleCalculateMatchday(pendingMatchday.id)}
                disabled={isCalculating}
                variant="default"
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg text-base py-6"
              >
                {isCalculating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Calcolo...
                  </>
                ) : (
                  <>
                    <Trophy className="mr-2 h-5 w-5" />
                    Calcola Giornata {pendingMatchday.matchday_number}
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Prossima Giornata */}
      {pendingMatchday && (
        <Card className="border-l-4 border-l-blue-600 shadow-lg">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Giornata in Corso
                </CardTitle>
                <CardDescription className="text-sm">Giornata {pendingMatchday.matchday_number}</CardDescription>
              </div>
              <Badge variant="outline" className="border-blue-600 text-blue-600 w-fit">
                In Corso
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingMatchday.matches.map((match, idx) => {
                const isMyMatch = match.team1_id === userTeam?.id || match.team2_id === userTeam?.id;
                const myTeamPoints = match.team1_id === userTeam?.id ? match.team1_points : match.team2_points;
                const opponentTeamPoints = match.team1_id === userTeam?.id ? match.team2_points : match.team1_points;
                
                return (
                  <div
                    key={match.id}
                    className={`p-4 sm:p-5 rounded-xl border-2 transition-all duration-300 ${
                      isMyMatch 
                        ? 'bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 dark:from-blue-950 dark:via-blue-900 dark:to-blue-950 border-blue-400 shadow-lg' 
                        : 'bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-700'
                    }`}
                  >
                    {/* Mobile Layout */}
                    <div className="block sm:hidden space-y-4">
                      {/* Team 1 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className={`h-14 w-14 ${match.team1_id === userTeam?.id ? 'bg-blue-600 ring-4 ring-blue-300' : 'bg-blue-500'}`}>
                            <AvatarFallback className="text-white text-base font-bold">
                              {match.team1_name.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-bold text-base text-gray-900 dark:text-gray-100">{match.team1_name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-300">{match.team1_owner_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                            {match.team1_points !== null && match.team1_points !== undefined ? Math.round(match.team1_points) : 0}
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300">punti</p>
                        </div>
                      </div>

                      {/* VS Divider */}
                      <div className="flex items-center justify-center">
                        <div className="flex-1 h-px bg-gray-300"></div>
                        <span className="px-4 text-xl font-bold text-gray-400">VS</span>
                        <div className="flex-1 h-px bg-gray-300"></div>
                      </div>

                      {/* Team 2 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className={`h-14 w-14 ${match.team2_id === userTeam?.id ? 'bg-cyan-600 ring-4 ring-cyan-300' : 'bg-cyan-500'}`}>
                            <AvatarFallback className="text-white text-base font-bold">
                              {match.team2_name.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-bold text-base text-gray-900 dark:text-gray-100">{match.team2_name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-300">{match.team2_owner_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400 animate-pulse">
                            {match.team2_points !== null && match.team2_points !== undefined ? Math.round(match.team2_points) : 0}
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300">punti</p>
                        </div>
                      </div>

                      {/* My Status - Solo se è il mio match */}
                      {isMyMatch && (
                        <>
                          <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg border-2 border-blue-400 dark:border-blue-600 animate-pulse">
                            <p className="text-center text-sm font-semibold text-blue-900 dark:text-blue-100">
                              📊 I tuoi punti: <span className="text-lg font-bold">{myTeamPoints !== null && myTeamPoints !== undefined ? Math.round(myTeamPoints) : 0}</span>
                            </p>
                            <p className="text-center text-xs text-gray-600 dark:text-gray-300 mt-1">
                              {myTeamPoints !== null && myTeamPoints !== undefined && opponentTeamPoints !== null && opponentTeamPoints !== undefined ? (
                                myTeamPoints > opponentTeamPoints ? (
                                  <span className="text-green-600 dark:text-green-400 font-semibold animate-bounce inline-block">🟢 Stai vincendo!</span>
                                ) : myTeamPoints < opponentTeamPoints ? (
                                  <span className="text-red-600 dark:text-red-400 font-semibold">🔴 Stai perdendo</span>
                                ) : (
                                  <span className="text-yellow-600 dark:text-yellow-400 font-semibold">🟡 In pareggio</span>
                                )
                              ) : (
                                'In attesa di punti...'
                              )}
                            </p>
                          </div>
                          
                          {/* Bottone Dettagli Punti - Mobile */}
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setBreakdownModal({
                                isOpen: true,
                                teamId: userTeam?.id,
                                teamName: userTeam ? teams.find(t => t.id === userTeam.id)?.name || 'Il tuo team' : 'Il tuo team'
                              });
                            }}
                            variant="outline"
                            className="w-full mt-3 border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950"
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Dettagli Punti
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <Avatar className={`h-16 w-16 ${match.team1_id === userTeam?.id ? 'bg-blue-600 ring-4 ring-blue-300' : 'bg-blue-500'}`}>
                          <AvatarFallback className="text-white text-lg font-bold">
                            {match.team1_name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{match.team1_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{match.team1_owner_name}</p>
                          <p className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                            {match.team1_points !== null && match.team1_points !== undefined ? Math.round(match.team1_points) : 0} pt
                          </p>
                        </div>
                      </div>

                      <div className="px-6 text-center">
                        <p className="text-2xl font-bold text-gray-400">VS</p>
                        {isMyMatch && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setBreakdownModal({
                                isOpen: true,
                                teamId: userTeam?.id,
                                teamName: userTeam ? teams.find(t => t.id === userTeam.id)?.name || 'Il tuo team' : 'Il tuo team'
                              });
                            }}
                            variant="outline"
                            size="sm"
                            className="mt-3 border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950"
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Dettagli
                          </Button>
                        )}
                      </div>

                      <div className="flex items-center gap-4 flex-1 justify-end">
                        <div className="text-right">
                          <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{match.team2_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{match.team2_owner_name}</p>
                          <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400 mt-1">
                            {match.team2_points !== null && match.team2_points !== undefined ? Math.round(match.team2_points) : 0} pt
                          </p>
                        </div>
                        <Avatar className={`h-16 w-16 ${match.team2_id === userTeam?.id ? 'bg-cyan-600 ring-4 ring-cyan-300' : 'bg-cyan-500'}`}>
                          <AvatarFallback className="text-white text-lg font-bold">
                            {match.team2_name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ultima Giornata */}
      {lastCalculated && (
        <Card className="border-l-4 border-l-green-600 shadow-lg">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Trophy className="h-5 w-5 text-green-600" />
                  Ultima Giornata
                </CardTitle>
                <CardDescription className="text-sm">Giornata {lastCalculated.matchday_number} - Risultati</CardDescription>
              </div>
              <Badge variant="outline" className="border-green-600 text-green-600 w-fit">
                Completata
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {lastCalculated.matches.map((match) => {
                const result = getMatchResult(match, userTeam?.id);
                const isAnimating = animatingMatch?.matchId === match.id;
                
                return (
                  <div
                    key={match.id}
                    className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl cursor-pointer ${
                      result
                        ? result.type === 'win'
                          ? 'bg-green-50 dark:bg-green-950 border-green-500 shadow-lg shadow-green-500/30 hover:shadow-green-500/50'
                          : result.type === 'draw'
                          ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-500 shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/50'
                          : 'bg-red-50 dark:bg-red-950 border-red-500 shadow-lg shadow-red-500/30 hover:shadow-red-500/50'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-300 hover:shadow-gray-500/30'
                    }`}
                  >
                    {/* Animazioni */}
                    {isAnimating && animatingMatch.type === 'win' && <WinAnimation />}
                    {isAnimating && animatingMatch.type === 'loss' && <LossAnimation />}
                    {isAnimating && animatingMatch.type === 'draw' && <DrawAnimation />}
                    
                    {result && (
                      <div className="mb-4">
                        <p className={`text-xl sm:text-2xl font-bold ${result.color} text-center`}>
                          {result.text}
                        </p>
                      </div>
                    )}
                    
                    {/* Mobile Layout */}
                    <div className="block sm:hidden space-y-4">
                      {/* Team 1 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-14 w-14 bg-blue-600 ring-2 ring-blue-400">
                            <AvatarFallback className="text-white text-base font-bold">
                              {match.team1_name.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-base text-gray-900 dark:text-gray-100">{match.team1_name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-300">{match.team1_owner_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 animate-pulse">
                            {match.team1_points !== null && match.team1_points !== undefined ? Math.round(match.team1_points) : 0}
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300">punti</p>
                        </div>
                      </div>

                      {/* Score Display */}
                      <div className="text-center py-2">
                        <p className="text-sm text-gray-500 mb-1">Punteggio Finale</p>
                        <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                          {match.team1_points !== null && match.team1_points !== undefined ? Math.round(match.team1_points) : 0} - {match.team2_points !== null && match.team2_points !== undefined ? Math.round(match.team2_points) : 0}
                        </p>
                      </div>

                      {/* Team 2 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-14 w-14 bg-cyan-600 ring-2 ring-cyan-400">
                            <AvatarFallback className="text-white text-base font-bold">
                              {match.team2_name.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-base text-gray-900 dark:text-gray-100">{match.team2_name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-300">{match.team2_owner_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                            {match.team2_points !== null && match.team2_points !== undefined ? Math.round(match.team2_points) : 0}
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300">punti</p>
                        </div>
                      </div>
                      
                      {/* Bottone Dettagli Punti - Mobile */}
                      {(match.team1_id === userTeam?.id || match.team2_id === userTeam?.id) && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setBreakdownModal({
                              isOpen: true,
                              teamId: userTeam?.id,
                              teamName: userTeam ? teams.find(t => t.id === userTeam.id)?.name || 'Il tuo team' : 'Il tuo team'
                            });
                          }}
                          variant="outline"
                          className="w-full mt-2 border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950"
                        >
                          <Search className="h-4 w-4 mr-2" />
                          Dettagli Punti
                        </Button>
                      )}
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <Avatar className="h-16 w-16 bg-blue-600 ring-2 ring-blue-400">
                          <AvatarFallback className="text-white text-lg font-bold">
                            {match.team1_name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{match.team1_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{match.team1_owner_name}</p>
                          <p className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-1">
                            {match.team1_points !== null && match.team1_points !== undefined ? Math.round(match.team1_points) : 0} pt
                          </p>
                        </div>
                      </div>

                      <div className="px-6 text-center">
                        <p className="text-sm text-gray-500 mb-1">Punti Fantasy</p>
                        <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                          {match.team1_points !== null && match.team1_points !== undefined ? Math.round(match.team1_points) : 0} - {match.team2_points !== null && match.team2_points !== undefined ? Math.round(match.team2_points) : 0}
                        </p>
                        {(match.team1_id === userTeam?.id || match.team2_id === userTeam?.id) && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setBreakdownModal({
                                isOpen: true,
                                teamId: userTeam?.id,
                                teamName: userTeam ? teams.find(t => t.id === userTeam.id)?.name || 'Il tuo team' : 'Il tuo team'
                              });
                            }}
                            variant="outline"
                            size="sm"
                            className="mt-3 border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950"
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Dettagli
                          </Button>
                        )}
                      </div>

                      <div className="flex items-center gap-4 flex-1 justify-end">
                        <div className="text-right">
                          <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{match.team2_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{match.team2_owner_name}</p>
                          <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400 mt-1">
                            {match.team2_points !== null && match.team2_points !== undefined ? Math.round(match.team2_points) : 0} pt
                          </p>
                        </div>
                        <Avatar className="h-16 w-16 bg-cyan-600 ring-2 ring-cyan-400">
                          <AvatarFallback className="text-white text-lg font-bold">
                            {match.team2_name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Bottone Annulla Giornata (solo per admin) */}
            {isAdmin && (
              <div className="mt-6 flex justify-center">
                <Button 
                  variant="outline" 
                  className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                  onClick={() => handleResetMatchday(lastCalculated.id)}
                  disabled={isCalculating}
                >
                  {isCalculating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetto...
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="mr-2 h-4 w-4" />
                      Annulla Giornata
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ultimi 5 Incontri */}
      {allMatchdays.length > 0 && (
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <TrendingUp className="h-5 w-5 text-gray-600" />
              Ultimi Incontri
            </CardTitle>
            <CardDescription className="text-sm">Scorri per vedere il tuo storico</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
              {allMatchdays.map((matchday, idx) => {
                const myMatch = findMyMatch(matchday);
                if (!myMatch) return null;

                const result = getMatchResult(myMatch, userTeam?.id);

                return (
                  <div
                    key={matchday.id}
                    onClick={() => {
                      setSelectedMatchday(matchday);
                      setShowMatchdayModal(true);
                    }}
                    className="flex-shrink-0 w-20 text-center transform transition-all duration-200 hover:scale-105 cursor-pointer"
                  >
                    <div
                      className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center text-white font-bold text-lg mb-2 shadow-lg transition-all duration-200 hover:shadow-xl ${
                        result?.type === 'win'
                          ? 'bg-gradient-to-br from-green-400 to-green-600 hover:shadow-green-500/50'
                          : result?.type === 'draw'
                          ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 hover:shadow-yellow-500/50'
                          : 'bg-gradient-to-br from-red-400 to-red-600 hover:shadow-red-500/50'
                      }`}
                    >
                      {matchday.matchday_number}
                    </div>
                    <p className="text-sm font-semibold mb-1.5 text-gray-700 dark:text-gray-300">
                      {myMatch.team1_points !== null && myMatch.team1_points !== undefined ? Math.round(myMatch.team1_points) : 0}-{myMatch.team2_points !== null && myMatch.team2_points !== undefined ? Math.round(myMatch.team2_points) : 0}
                    </p>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {(() => {
                        const isTeam1 = myMatch.team1_id === userTeam?.id;
                        const myPoints = isTeam1 ? myMatch.team1_points : myMatch.team2_points;
                        return myPoints !== null && myPoints !== undefined ? Math.round(myPoints) : 0;
                      })()}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendario Completo Giornate */}
      {matchdays.length > 0 && (
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Calendar className="h-5 w-5 text-blue-600" />
              Calendario Completo
            </CardTitle>
            <CardDescription className="text-sm">
              Tutte le giornate: passate, in corso e future
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {matchdays.map((matchday) => {
                const myMatch = findMyMatch(matchday);
                if (!myMatch) return null;

                const isCompleted = matchday.status === 'calculated';
                const isPending = matchday.status === 'pending';
                const result = isCompleted ? getMatchResult(myMatch, userTeam?.id) : null;
                const isTeam1 = myMatch.team1_id === userTeam?.id;
                const opponent = isTeam1 ? {
                  name: myMatch.team2_name,
                  owner: myMatch.team2_owner_name
                } : {
                  name: myMatch.team1_name,
                  owner: myMatch.team1_owner_name
                };

                return (
                  <div
                    key={matchday.id}
                    onClick={() => {
                      setSelectedMatchday(matchday);
                      setShowMatchdayModal(true);
                    }}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] ${
                      isPending
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 hover:border-blue-500'
                        : isCompleted && result?.type === 'win'
                        ? 'border-green-400 bg-green-50/50 dark:bg-green-950/20 hover:border-green-500'
                        : isCompleted && result?.type === 'draw'
                        ? 'border-yellow-400 bg-yellow-50/50 dark:bg-yellow-950/20 hover:border-yellow-500'
                        : isCompleted && result?.type === 'loss'
                        ? 'border-red-400 bg-red-50/50 dark:bg-red-950/20 hover:border-red-500'
                        : 'border-gray-300 bg-gray-50 dark:bg-gray-900'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={isPending ? 'default' : 'outline'}
                          className={
                            isPending
                              ? 'bg-blue-600'
                              : isCompleted
                              ? 'border-green-600 text-green-700 bg-green-50 dark:bg-green-950'
                              : ''
                          }
                        >
                          Giornata {matchday.matchday_number}
                        </Badge>
                        {isPending && (
                          <Badge variant="outline" className="border-yellow-500 text-yellow-700 bg-yellow-50">
                            In Corso
                          </Badge>
                        )}
                      </div>
                      {isCompleted && result && (
                        <Badge
                          className={
                            result.type === 'win'
                              ? 'bg-green-600'
                              : result.type === 'draw'
                              ? 'bg-yellow-600'
                              : 'bg-red-600'
                          }
                        >
                          {result.type === 'win' ? '✓ Vinta' : result.type === 'draw' ? '= Pareggio' : '✗ Persa'}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 bg-blue-600">
                          <AvatarFallback className="text-white text-sm font-bold">
                            {opponent.name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-bold text-sm">{opponent.name}</p>
                          <p className="text-xs text-muted-foreground">{opponent.owner}</p>
                        </div>
                      </div>

                      {isCompleted && (
                        <div className="text-right">
                          <p className="text-lg font-bold text-primary">
                            {isTeam1 
                              ? `${Math.round(myMatch.team1_points || 0) || '0'} - ${Math.round(myMatch.team2_points || 0) || '0'}`
                              : `${Math.round(myMatch.team2_points || 0) || '0'} - ${Math.round(myMatch.team1_points || 0) || '0'}`
                            }
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {isTeam1 
                              ? `Tu: ${Math.round(myMatch.team1_points || 0) || '0'}`
                              : `Tu: ${Math.round(myMatch.team2_points || 0) || '0'}`
                            }
                          </p>
                        </div>
                      )}

                      {isPending && (
                        <div className="text-right">
                          <Badge variant="outline" className="border-blue-500 text-blue-700">
                            Prossimo Avversario
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {matchdays.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nessuna giornata disponibile</h3>
              <p className="text-gray-500">
                {isAdmin
                  ? 'Crea la prima giornata per iniziare il campionato!'
                  : 'Aspetta che un admin crei la prima giornata.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal Dettagli Giornata */}
      <MatchdayDetailsModal
        isOpen={showMatchdayModal}
        onClose={() => setShowMatchdayModal(false)}
        matchday={selectedMatchday}
        leagueId={leagueId}
        token={token}
        userTeam={userTeam}
      />
      
      {/* Modal Dettagli Punti Team */}
      <TeamEventsBreakdownModal
        isOpen={breakdownModal.isOpen}
        onClose={() => setBreakdownModal({ isOpen: false, teamId: null, teamName: '' })}
        teamId={breakdownModal.teamId}
        teamName={breakdownModal.teamName}
        token={token}
      />
    </div>
  );
});

GiornateTab.displayName = 'GiornateTab';
